import { useState, useEffect, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  SurveyStatus,
  FindingStatus,
  FindingSeverity,
  FindingCategory,
  VulnerabilityFinding,
} from '@dsr/shared-types';
import {
  useSurvey,
  useUpdateSurvey,
  useTransitionSurvey,
  useFindings,
  useCreateFinding,
  useUpdateFinding,
  useDeleteFinding,
  useSitesLookup,
} from '../api/queries/surveys';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import {
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  FormField,
  FormRow,
  Input,
  KpiCard,
  Select,
  SeverityBadge,
  StatusBadge,
  Table,
  Textarea,
  type SelectOption,
} from '../components/ui';
import './SurveysPage.css';

const STATUS_LABEL: Record<SurveyStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
};

// Forward-only workflow: each status offers the next move.
const NEXT_STATUS: Partial<Record<SurveyStatus, { to: SurveyStatus; label: string }>> = {
  draft: { to: 'submitted', label: 'Submit for review' },
  submitted: { to: 'approved', label: 'Approve' },
};

const FINDING_STATUS_LABEL: Record<FindingStatus, string> = {
  open: 'Open',
  mitigated: 'Mitigated',
  accepted: 'Accepted',
  closed: 'Closed',
};
const FINDING_STATUSES: FindingStatus[] = ['open', 'mitigated', 'accepted', 'closed'];

const SEVERITY_LABEL: Record<FindingSeverity, string> = {
  info: 'Info',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};
const SEVERITIES: FindingSeverity[] = ['info', 'low', 'medium', 'high', 'critical'];

const CATEGORY_LABEL: Record<FindingCategory, string> = {
  perimeter: 'Perimeter',
  lighting: 'Lighting',
  access_control: 'Access control',
  cctv: 'CCTV',
  staffing: 'Staffing',
  policy: 'Policy',
  cyber_physical: 'Cyber-physical',
  other: 'Other',
};
const CATEGORIES: FindingCategory[] = [
  'perimeter', 'lighting', 'access_control', 'cctv',
  'staffing', 'policy', 'cyber_physical', 'other',
];

function fmtDate(epochMs: number | null | undefined): string {
  if (epochMs === null || epochMs === undefined || !Number.isFinite(epochMs)) return '—';
  const d = new Date(epochMs);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
}

function firstFieldError(err: unknown): string | null {
  if (err instanceof ApiError && err.status === 400) {
    const body = err.body as
      | { error?: string; details?: { fieldErrors?: Record<string, string[]> } }
      | null;
    const fe = body?.details?.fieldErrors;
    if (fe) {
      const first = Object.values(fe).find((a) => Array.isArray(a) && a.length > 0);
      if (first && Array.isArray(first)) return first[0]!;
    }
    if (body?.error) return body.error;
  }
  return null;
}

export function SurveyDetailPage(): JSX.Element {
  const { id: idRaw } = useParams<{ id: string }>();
  const idNum = idRaw ? Number(idRaw) : NaN;
  const id = Number.isInteger(idNum) && idNum > 0 ? idNum : null;

  const { data, isPending, error } = useSurvey(id);
  const sitesQuery = useSitesLookup();
  const updateSurvey = useUpdateSurvey();
  const transition = useTransitionSurvey();

  // ---- Finding filters ----
  // Radix Select forbids an empty value, so 'all' is the no-filter sentinel.
  const [findStatus, setFindStatus] = useState<FindingStatus | 'all'>('all');
  const [findSeverity, setFindSeverity] = useState<FindingSeverity | 'all'>('all');
  const [findCategory, setFindCategory] = useState<FindingCategory | 'all'>('all');

  const findingsQuery = useFindings(id, {
    ...(findStatus !== 'all' ? { status: findStatus } : {}),
    ...(findSeverity !== 'all' ? { severity: findSeverity } : {}),
    ...(findCategory !== 'all' ? { category: findCategory } : {}),
  });
  const createFinding = useCreateFinding();
  const updateFinding = useUpdateFinding();
  const deleteFinding = useDeleteFinding();

  // ---- Edit survey modal ----
  const [showEdit, setShowEdit] = useState(false);
  const [editSurveyor, setEditSurveyor] = useState('');
  const [editConductedAt, setEditConductedAt] = useState<number | null>(null);
  const [editSummary, setEditSummary] = useState('');
  const [editDocPath, setEditDocPath] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  const survey = data?.survey;

  useEffect(() => {
    if (survey && showEdit) {
      setEditSurveyor(survey.surveyor_user_id ? String(survey.surveyor_user_id) : '');
      setEditConductedAt(survey.conducted_at);
      setEditSummary(survey.summary ?? '');
      setEditDocPath(survey.document_path ?? '');
      setEditError(null);
    }
  }, [survey, showEdit]);

  // ---- Finding create/edit modal ----
  const [findingModal, setFindingModal] = useState<
    { mode: 'create' } | { mode: 'edit'; finding: VulnerabilityFinding } | null
  >(null);
  const [fSeverity, setFSeverity] = useState<FindingSeverity>('low');
  const [fStatus, setFStatus] = useState<FindingStatus>('open');
  const [fCategory, setFCategory] = useState<FindingCategory | ''>('');
  const [fRecommendation, setFRecommendation] = useState('');
  const [findingError, setFindingError] = useState<string | null>(null);

  const [deleteFindingId, setDeleteFindingId] = useState<number | null>(null);

  const openCreateFinding = () => {
    setFSeverity('low');
    setFStatus('open');
    setFCategory('');
    setFRecommendation('');
    setFindingError(null);
    setFindingModal({ mode: 'create' });
  };

  const openEditFinding = (f: VulnerabilityFinding) => {
    setFSeverity(f.severity);
    setFStatus(f.status);
    setFCategory(f.category ?? '');
    setFRecommendation(f.recommendation ?? '');
    setFindingError(null);
    setFindingModal({ mode: 'edit', finding: f });
  };

  if (id === null) return <p className="sv-status sv-error">Invalid survey id.</p>;
  if (error) {
    if (error instanceof ApiError && error.status === 404) {
      return <p className="sv-status sv-error">Survey not found.</p>;
    }
    if (error instanceof ApiError && error.status === 403) {
      return <p className="sv-status sv-error">You don't have permission to view this survey.</p>;
    }
    return <p className="sv-status sv-error">Failed to load survey.</p>;
  }
  if (isPending || !survey) return <p className="sv-status">Loading survey…</p>;

  const sites = sitesQuery.data?.sites ?? [];
  const siteName = sites.find((s) => s.id === survey.site_id)?.name ?? `Site #${survey.site_id}`;

  const findings = findingsQuery.data?.findings ?? [];
  const openCount = findings.filter((f) => f.status === 'open').length;
  const criticalCount = findings.filter((f) => f.severity === 'critical').length;
  const highCount = findings.filter((f) => f.severity === 'high').length;

  const next = NEXT_STATUS[survey.status];

  const severityFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All severities' },
    ...SEVERITIES.map((s) => ({ value: s, label: SEVERITY_LABEL[s] })),
  ];
  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    ...FINDING_STATUSES.map((s) => ({ value: s, label: FINDING_STATUS_LABEL[s] })),
  ];
  const categoryFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All categories' },
    ...CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABEL[c] })),
  ];

  // ---- Handlers ----
  const onSaveEdit = async (e: FormEvent) => {
    e.preventDefault();
    setEditError(null);
    const surveyor = editSurveyor.trim();
    if (surveyor && !/^\d+$/.test(surveyor)) {
      setEditError('Surveyor user ID must be a number.');
      return;
    }
    try {
      await updateSurvey.mutateAsync({
        id,
        surveyor_user_id: surveyor ? Number(surveyor) : null,
        conducted_at: editConductedAt,
        summary: editSummary.trim() ? editSummary.trim() : null,
        document_path: editDocPath.trim() ? editDocPath.trim() : null,
      });
      toast.success('Survey updated.');
      setShowEdit(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setEditError("You don't have permission to edit this survey.");
        return;
      }
      setEditError(firstFieldError(err) ?? 'Could not save changes.');
    }
  };

  const onTransition = async (to: SurveyStatus) => {
    try {
      await transition.mutateAsync({ id, status: to });
      toast.success(`Survey ${STATUS_LABEL[to].toLowerCase()}.`);
    } catch (err) {
      const msg =
        err instanceof ApiError && err.status === 403
          ? "You don't have permission to change survey status."
          : 'Could not change status.';
      toast.danger(msg);
    }
  };

  const onSaveFinding = async (e: FormEvent) => {
    e.preventDefault();
    if (!findingModal) return;
    setFindingError(null);
    try {
      if (findingModal.mode === 'create') {
        await createFinding.mutateAsync({
          surveyId: id,
          severity: fSeverity,
          status: fStatus,
          ...(fCategory ? { category: fCategory } : {}),
          ...(fRecommendation.trim() ? { recommendation: fRecommendation.trim() } : {}),
        });
        toast.success('Finding added.');
      } else {
        await updateFinding.mutateAsync({
          surveyId: id,
          findingId: findingModal.finding.id,
          severity: fSeverity,
          status: fStatus,
          category: fCategory ? fCategory : null,
          recommendation: fRecommendation.trim() ? fRecommendation.trim() : null,
        });
        toast.success('Finding updated.');
      }
      setFindingModal(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setFindingError("You don't have permission to edit findings.");
        return;
      }
      setFindingError(firstFieldError(err) ?? 'Could not save finding.');
    }
  };

  const onConfirmDeleteFinding = async () => {
    if (deleteFindingId === null) return;
    try {
      await deleteFinding.mutateAsync({ surveyId: id, findingId: deleteFindingId });
      toast.success('Finding deleted.');
    } catch (err) {
      const msg =
        err instanceof ApiError && err.status === 403
          ? "You don't have permission to delete findings."
          : 'Could not delete finding.';
      toast.danger(msg);
    } finally {
      setDeleteFindingId(null);
    }
  };

  const savingFinding = createFinding.isPending || updateFinding.isPending;

  return (
    <section className="sv-page">
      <div>
        <Link to="/surveys" className="sv-back">
          ← Surveys
        </Link>
      </div>

      {/* ---- Survey header ---- */}
      <Card>
        <Card.Body>
          <div className="sv-detail-top">
            <div>
              <h2>{siteName}</h2>
              <p className="sv-sub">Site survey #{survey.id}</p>
            </div>
            <div className="sv-header-actions">
              <StatusBadge status={survey.status}>{STATUS_LABEL[survey.status]}</StatusBadge>
              {can('survey.write') && (
                <Button variant="ghost" onClick={() => setShowEdit(true)}>
                  Edit
                </Button>
              )}
              {next && can('survey.approve') && (
                <Button
                  variant="primary"
                  onClick={() => onTransition(next.to)}
                  disabled={transition.isPending}
                >
                  {transition.isPending ? 'Working…' : next.label}
                </Button>
              )}
            </div>
          </div>

          <dl className="detail-grid">
            <div>
              <dt>Surveyor</dt>
              <dd>{survey.surveyor_name ?? 'Unassigned'}</dd>
            </div>
            <div>
              <dt>Conducted</dt>
              <dd>{fmtDate(survey.conducted_at)}</dd>
            </div>
            <div>
              <dt>Created</dt>
              <dd>{fmtDate(survey.created_at)}</dd>
            </div>
            <div>
              <dt>Document</dt>
              <dd>{survey.document_path ? <code>{survey.document_path}</code> : '—'}</dd>
            </div>
          </dl>

          {survey.summary && <div className="sv-summary">{survey.summary}</div>}
        </Card.Body>
      </Card>

      {/* ---- Findings ---- */}
      <Card>
        <Card.Body>
          <div className="sv-section-head">
            <h3>Vulnerability findings</h3>
            {can('survey.write') && (
              <Button variant="primary" onClick={openCreateFinding}>
                Add finding
              </Button>
            )}
          </div>

          <div className="kpi-grid">
            <KpiCard label="Total" value={findings.length} />
            <KpiCard label="Open" value={openCount} />
            <KpiCard label="Critical" value={criticalCount} tone="danger" />
            <KpiCard label="High" value={highCount} tone="warn" />
          </div>

          <div className="filters-bar">
            <label className="sv-filter">
              <span>Severity</span>
              <Select
                value={findSeverity}
                onValueChange={(v) => setFindSeverity(v as FindingSeverity | 'all')}
                options={severityFilterOptions}
                aria-label="Filter by severity"
              />
            </label>
            <label className="sv-filter">
              <span>Status</span>
              <Select
                value={findStatus}
                onValueChange={(v) => setFindStatus(v as FindingStatus | 'all')}
                options={statusFilterOptions}
                aria-label="Filter by status"
              />
            </label>
            <label className="sv-filter">
              <span>Category</span>
              <Select
                value={findCategory}
                onValueChange={(v) => setFindCategory(v as FindingCategory | 'all')}
                options={categoryFilterOptions}
                aria-label="Filter by category"
              />
            </label>
          </div>

          {findingsQuery.isPending ? (
            <p className="sv-status">Loading findings…</p>
          ) : findingsQuery.error ? (
            <p className="sv-status sv-error">Failed to load findings.</p>
          ) : findings.length === 0 ? (
            <div className="sv-empty">
              <p>No findings recorded for this survey.</p>
              {can('survey.write') && (
                <p className="sv-empty-meta">Use "Add finding" to log a vulnerability.</p>
              )}
            </div>
          ) : (
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.HeaderCell>Severity</Table.HeaderCell>
                  <Table.HeaderCell>Category</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Recommendation</Table.HeaderCell>
                  {can('survey.write') && <Table.HeaderCell></Table.HeaderCell>}
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {findings.map((f) => (
                  <Table.Row key={f.id}>
                    <Table.Cell>
                      <SeverityBadge level={f.severity}>{SEVERITY_LABEL[f.severity]}</SeverityBadge>
                    </Table.Cell>
                    <Table.Cell>
                      {f.category ? (
                        <span className="sv-cat-chip">{CATEGORY_LABEL[f.category]}</span>
                      ) : (
                        <span className="sv-cell-muted">—</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <StatusBadge status={f.status}>{FINDING_STATUS_LABEL[f.status]}</StatusBadge>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="sv-recommendation">{f.recommendation ?? '—'}</div>
                    </Table.Cell>
                    {can('survey.write') && (
                      <Table.Cell className="sv-actions-cell">
                        <button
                          type="button"
                          className="sv-link"
                          onClick={() => openEditFinding(f)}
                        >
                          edit
                        </button>
                        <button
                          type="button"
                          className="sv-link sv-link-danger"
                          onClick={() => setDeleteFindingId(f.id)}
                          disabled={deleteFinding.isPending}
                        >
                          delete
                        </button>
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* ---- Edit survey modal ---- */}
      <Dialog
        title="Edit survey"
        open={showEdit}
        busy={updateSurvey.isPending}
        onClose={() => setShowEdit(false)}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setShowEdit(false)}
              disabled={updateSurvey.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="sv-edit-form"
              variant="primary"
              disabled={updateSurvey.isPending}
            >
              {updateSurvey.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        }
      >
        <form id="sv-edit-form" className="sv-form" onSubmit={onSaveEdit}>
          <FormRow>
            <FormField label="Surveyor user ID">
              <Input
                type="text"
                value={editSurveyor}
                onChange={(e) => setEditSurveyor(e.target.value)}
                placeholder="optional"
                inputMode="numeric"
                pattern="\d*"
                disabled={updateSurvey.isPending}
              />
            </FormField>
            <FormField label="Conducted on">
              <DateInput
                value={editConductedAt}
                onChange={setEditConductedAt}
                disabled={updateSurvey.isPending}
              />
            </FormField>
          </FormRow>
          <FormField label="Document path">
            <Input
              type="text"
              value={editDocPath}
              onChange={(e) => setEditDocPath(e.target.value)}
              placeholder="optional — vault path to the report deliverable"
              maxLength={1000}
              disabled={updateSurvey.isPending}
            />
          </FormField>
          <FormField label="Summary">
            <Textarea
              className="sv-textarea"
              value={editSummary}
              onChange={(e) => setEditSummary(e.target.value)}
              rows={5}
              maxLength={20_000}
              disabled={updateSurvey.isPending}
            />
          </FormField>
          {editError && (
            <p className="sv-error-banner" role="alert">{editError}</p>
          )}
        </form>
      </Dialog>

      {/* ---- Create / edit finding modal ---- */}
      <Dialog
        title={findingModal?.mode === 'edit' ? 'Edit finding' : 'Add finding'}
        open={findingModal !== null}
        busy={savingFinding}
        onClose={() => setFindingModal(null)}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setFindingModal(null)}
              disabled={savingFinding}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="sv-finding-form"
              variant="primary"
              disabled={savingFinding}
            >
              {savingFinding ? 'Saving…' : 'Save finding'}
            </Button>
          </>
        }
      >
        <form id="sv-finding-form" className="sv-form" onSubmit={onSaveFinding}>
          <FormRow>
            <FormField label="Severity" required>
              <select
                className="form-input"
                value={fSeverity}
                onChange={(e) => setFSeverity(e.target.value as FindingSeverity)}
                disabled={savingFinding}
              >
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>{SEVERITY_LABEL[s]}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Status" required>
              <select
                className="form-input"
                value={fStatus}
                onChange={(e) => setFStatus(e.target.value as FindingStatus)}
                disabled={savingFinding}
              >
                {FINDING_STATUSES.map((s) => (
                  <option key={s} value={s}>{FINDING_STATUS_LABEL[s]}</option>
                ))}
              </select>
            </FormField>
          </FormRow>
          <FormField label="Category">
            <select
              className="form-input"
              value={fCategory}
              onChange={(e) => setFCategory(e.target.value as FindingCategory | '')}
              disabled={savingFinding}
            >
              <option value="">Uncategorized</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Recommendation">
            <Textarea
              className="sv-textarea"
              value={fRecommendation}
              onChange={(e) => setFRecommendation(e.target.value)}
              rows={4}
              maxLength={20_000}
              placeholder="Remediation guidance for this vulnerability…"
              disabled={savingFinding}
            />
          </FormField>
          {findingError && (
            <p className="sv-error-banner" role="alert">{findingError}</p>
          )}
        </form>
      </Dialog>

      <ConfirmDialog
        open={deleteFindingId !== null}
        title="Delete finding"
        message="Delete this finding? This can't be undone from here."
        confirmLabel="Delete"
        danger
        busy={deleteFinding.isPending}
        onConfirm={onConfirmDeleteFinding}
        onCancel={() => setDeleteFindingId(null)}
      />
    </section>
  );
}
